import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  getWeightMeasurementById,
  GetWeightMeasurementDetailsParams,
  getWeightMeasurements,
} from 'services/measurements/weight';
import { MEASUREMENT_REFETCH_INTERVAL, queryKeys } from 'utils/query';
import { usePatientDetailContext, useDrawerContext } from 'context/hooks';
import { WeightIndicator, WeightIndicatorMap, WeightMeasurement } from 'models/weight';
import { calculateBmiIndicator } from 'utils/helpers';
import { DistributionValues } from 'models/charts';
import { t } from 'i18next';
import { useCallback } from 'react';
import { renderWeightDetails } from 'pages/Main/Patients/Details/Overview/WeightBmiOverview/WeightDetails.helpers';
import { usePatient } from './useUser';

export const useWeightMeasurements = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const { data: patientData } = usePatient({ patientId });
  const { startDate, endDate, unitSystem } = usePatientDetailContext();
  const { openDrawer } = useDrawerContext();

  const groupIds = patientData?.weightEnabledGroupsIds;
  const isEnabled = (groupIds && groupIds.length > 0) || false;

  const query = useQuery({
    queryKey: [queryKeys.measurementWeightData, patientId, groupIds, startDate, endDate],
    queryFn: () => getWeightMeasurements({ patientId, groupIds, startDate, endDate }),
    refetchInterval: MEASUREMENT_REFETCH_INTERVAL,
    enabled: !!patientId,
  });

  const { data } = query;

  const getMeasurementsByIndicator = (indicator: WeightIndicator) =>
    data?.filter((measurement) => calculateBmiIndicator(measurement.data.value) === indicator);

  const distributionValues: DistributionValues<WeightIndicator> = {
    total: data?.length || 0,
    values: Object.values(WeightIndicator)
      .filter((value) => value !== WeightIndicator.NoResult)
      .map((label) => {
        return {
          label,
          total: getMeasurementsByIndicator(label)?.length || 0,
          color: WeightIndicatorMap[label].color,
          name: t(`indicator_label_${label}`),
          Icon: WeightIndicatorMap[label].Icon,
        };
      }),
  };

  const handleSelect = (item: WeightMeasurement) => {
    if (item.id) {
      openDrawer({
        content: renderWeightDetails({ moreIds: data?.map((value) => value.id), unitSystem }),
        detailId: item.id,
      });
    }
  };

  const getItemById = useCallback(
    (id: string) => {
      return data ? data.find((item) => item.id === id) : {};
    },
    [data],
  );

  return { ...query, isEnabled, handleSelect, getMeasurementsByIndicator, distributionValues, getItemById };
};

export const useWeightMeasurementById = (params: GetWeightMeasurementDetailsParams) =>
  useQuery({
    queryKey: [queryKeys.measurementWeightData, params.id],
    queryFn: () => getWeightMeasurementById(params),
    refetchInterval: MEASUREMENT_REFETCH_INTERVAL,
    enabled: !!params.id,
  });
